import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from '../../redux/store'
import { setCompareProductFilter } from '../../redux/slices/ClusterSlice'
import { FilterType } from '../../utils/compareProductFilter'
import Dialog from '../UI/Dialog/Dialog'
import {
	FilterWrapper,
	FilterButton,
	FilterIconWrapper,
	ChevronIcon,
	FilterLabel,
} from './CompareProductFilter.style'
import {
	StyledForm,
	StyledInput,
	StyledLabel,
} from '../UI/RadioForm/RadioForm.style'
import { useCompareSkuCategories } from '../../hooks/useCompareSkuCategories'

const CompareProductFilter: React.FC<{ showCompareFilter: boolean }> = ({
	showCompareFilter,
}) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()

	const [dialogOpen, setDialogOpen] = useState(false)

	const currentFilter = useSelector(
		(state: AppState) => state.cluster.compareProductFilter,
	)

	// Options availability
	const { sharedUPCs, uniqueUPCs } = useCompareSkuCategories()

	const filterOptions = useMemo(
		() => [
			{
				value: 'all' as FilterType,
				label: t('compare.filter.all'),
				disabled: false,
			},
			{
				value: 'shared' as FilterType,
				label: t('compare.filter.shared'),
				disabled: sharedUPCs.size === 0,
			},
			{
				value: 'unique' as FilterType,
				label: t('compare.filter.unique'),
				disabled: uniqueUPCs.size === 0,
			},
		],
		[t, sharedUPCs.size, uniqueUPCs.size],
	)

	const handleFilterChange = (value: FilterType) => {
		dispatch(setCompareProductFilter(value))
		setDialogOpen(false)
	}

	const currentFilterLabel = filterOptions.find(
		opt => opt.value === currentFilter,
	)?.label

	return (
		<FilterWrapper isVisible={showCompareFilter}>
			<FilterLabel>{t('compare.filter.label')}</FilterLabel>
			<FilterButton onClick={() => setDialogOpen(true)}>
				{currentFilterLabel}
				<FilterIconWrapper>
					<ChevronIcon />
				</FilterIconWrapper>
			</FilterButton>

			<Dialog
				isOpen={dialogOpen}
				onClose={() => setDialogOpen(false)}
				top={100}
				left={30}
			>
				<StyledForm>
					{filterOptions.map(option => (
						<StyledLabel
							key={option.value}
							checked={currentFilter === option.value}
							style={{
								opacity: option.disabled ? 0.5 : 1,
								cursor: option.disabled ? 'not-allowed' : 'pointer',
							}}
						>
							<StyledInput
								type="radio"
								name="product-filter"
								value={option.value}
								checked={currentFilter === option.value}
								disabled={option.disabled}
								onChange={e => {
									if (!option.disabled) {
										handleFilterChange(e.target.value as FilterType)
									}
								}}
							/>
							{option.label}
						</StyledLabel>
					))}
				</StyledForm>
			</Dialog>
		</FilterWrapper>
	)
}

export default CompareProductFilter
